import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSCallbacks = () => {
    const iocPatternCode = `// --- Inversion of Control (IoC) Danger ---
function thirdPartyAnalytics(callback) {
    //  We have no control over this internal logic
    // It might call our callback 5 times, or 0 times!
    setTimeout(() => {
        callback("Data Tracked");
    }, 100);
}

thirdPartyAnalytics((msg) => {
    console.log(msg); // How do we guarantee this only runs once?
});`;

    const errorFirstCode = `// --- Node.js Error-First Convention ---
function secureFetch(query, callback) {
    if (!query) {
        return callback(new Error("Query missing"), null);
    }

    setTimeout(() => {
        const data = { id: 101, status: "Active" };
        callback(null, data); // Error is null, data is second argument
    }, 500);
}

secureFetch(null, (err, data) => {
    if (err) return console.error(\`Failure: \${err.message}\`);
    console.log(\`Success: \${data.status}\`);
});`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Legacy Patterns</div>
                <h1>The Callback Paradigm</h1>
                <p className="hero-lead">
                    Before the elegance of Promises and the modern syntax of
                    Async/Await, JavaScript relied entirely on <strong>Callbacks</strong>.
                    While conceptually simplepassing a function as a
                    valuethey introduce a fundamental shift in authority
                    known as Inversion of Control.
                </p>
            </div>

            <div className="content-section">
                <h2>The Mechanics of Delegation</h2>
                <p>
                    A callback is fundamentally a function passed as an
                    argument to another function with the intent that it will
                    be "called back" at some point in the future. This pattern
                    is the primary mechanism for asynchronous delegation in
                    early JavaScript, allowing the engine to continue
                    executing other code while waiting for an external
                    operation to complete. The calling function delegates a
                    piece of logic to the receiving function, essentially
                    saying: "I don't know exactly when this task will finish,
                    so here is the code you should run when it does." However,
                    this simplicity masks a deeper architectural challenge
                    regarding state management and execution order. Because the
                    callback is executed outside its original temporal context,
                    relying on it for complex workflows requires careful
                    scoping and memory management. Despite these challenges,
                    callbacks remain the ultimate building block of almost all
                    higher-level async abstractions.
                </p>
                <CodeBlock language="javascript" code={iocPatternCode} />
            </div>

            <div className="content-section">
                <h2>The Inversion of Control (IoC)</h2>
                <p>
                    The most critical issue with callbacks is not their
                    appearance, but the loss of control they represent for the
                    developer. When you pass a callback to a third-party
                    utility, you are essentially trust-falling into their
                    implementation details. You have no programmatic guarantee
                    that your function will be called exactly once, at the
                    expected time, or with the correct data. If an external
                    library has a bug and calls your "payment success"
                    callback twice, your user might be double-charged, and
                    your code has very few ways to prevent this natively. This
                    lack of "Inversion of Control" is exactly what modern
                    Promises were designed to solve by returning control back
                    to the caller. As a senior developer, you must treat every
                    callback passed to an external source as a potential
                    security and logic risk.
                </p>
                <ProTip><strong> Engineering Pro-Tip:</strong> To protect against
                    IoC issues, always implement "defensive callbacks." Use a
                    boolean flag or a wrapper function (like <code>_.once()</code>)
                    to ensure your business logic only executes a single time,
                    regardless of how many times the underlying agency triggers
                    the event.</ProTip>
            </div>

            <div className="content-section">
                <h2>Error Handling: The Error-First Pattern</h2>
                <p>
                    In the pre-Promise era, there was no standardized way to
                    propagate errors through a chain of asynchronous
                    operations. Node.js popularized the <strong>Error-First
                        Callback</strong> convention, where the first parameter of
                    every callback is reserved for a potential error object. If
                    the operation succeeds, the first argument is <code>null</code>
                    or <code>undefined</code>, and the data is passed as the
                    subsequent arguments. This pattern forces developers to
                    manually check for failures at every single step of the
                    execution chain, which is both tedious and prone to human
                    error. If a single check is forgotten, the failure can
                    silently propagate, leading to "zombie" processes that fail
                    to finish or throw confusing stack traces later. While
                    modern JavaScript uses <code>try/catch</code> blocks, you
                    will still encounter this pattern in thousands of legacy
                    NPM packages and internal Node.js modules. Mastering this
                    pattern is essential for anyone maintaining or integrating
                    with classic server-side JavaScript code.
                </p>
                <CodeBlock language="javascript" code={errorFirstCode} />
            </div>

            <div className="content-section">
                <h2>The "Pyramid of Doom"</h2>
                <p>
                    The term "Callback Hell" refers to the literal physical
                    shape that code takes when multiple asynchronous operations
                    are nested within one another. As each step requires the
                    result of the previous step, the code marches relentlessly
                    to the right side of the editor, forming a triangle-like
                    structure known as the "Pyramid of Doom." This isn't just
                    an aesthetic problem; its a cognitive one, as the
                    execution flow becomes incredibly difficult to read, track,
                    and debug. Developers must mentally manage multiple
                    closures, variables from outer scopes, and repetitive
                    error handling logic at every level of indentation. Each
                    additional step in the process exponentially increases the
                    complexity and the chance of introducing a bug. This
                    friction eventually became the primary catalyst for the
                    creation of Promises, which allowed these "nested"
                    triangles to be flattened into linear, readable chains.
                </p>
            </div>

            <KeyFacts><ul>
                    <li><strong>Trust Nothing:</strong> Always assume a third-party callback might fail to fire or fire multiple times.</li>
                    <li><strong>Guard Closures:</strong> Be mindful of variable shadowing and memory leaks when nesting multiple levels deep.</li>
                    <li><strong>Handle Mid-Chain:</strong> Never skip error checks; a single <code>null</code> reference can crash the entire sequence.</li>
                    <li><strong>Standardize:</strong> Follow the Error-First pattern (err, data) for consistency even in your own utilities.</li>
                    <li><strong>Transition Path:</strong> When possible, use <code>util.promisify</code> to convert legacy callbacks into modern Promises.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSCallbacks;

